import { createSelector } from '@reduxjs/toolkit';
import { getCellKey } from '@utils/grid';
import { CellCoordinates, Column, ColumnType, Feature, FilterConditionType, FilterState, PluginFeature, Row, SortState } from '../../types/Spreadsheet';
import { RootState } from '../store';

// Import existing selectors
import { CellStatusType } from './cellChangesSlice';
import { selectCutCells } from './clipboardSlice';
import { selectCollapsedGroups } from './collapsedGroupsSlice';
import { selectAllColumns } from './columnsSlice';
import { selectEditingCell } from './editingSlice';
import { selectFilters } from './filtersSlice';
import { selectFocusedCell } from './focusSlice';
import { selectGroupBy } from './groupBySlice';
import { selectAllPluginFeatures } from './pluginFeaturesSlice';
import { selectAllRows, selectRowById, selectRowEntities } from './rowsSlice';
import { selectSelection, SelectionRange } from './selectionSlice';
import { selectSorting } from './sortingSlice';

function applyFilters( rows: Row[], filters: FilterState ): Row[] {
  if ( !filters || filters.length === 0 ) return rows;

  return rows.filter(( row ) => {
    let overallResult = false;
    let firstCondition = true;

    for ( let i = 0; i < filters.length; i++ ) {
      const f = filters[i];
      if ( !f.operator || ( f.operator !== 'exists' && ( f.value == null || f.value === '' ))) {
        continue;
      }

      const conditionMet = doesRowMeetFilter( row, f );

      if ( firstCondition ) {
        overallResult = conditionMet;
        firstCondition = false;
      } else {
        if ( f.concatenator === 'and' ) {
          overallResult = overallResult && conditionMet;
        } else if ( f.concatenator === 'or' ) {
          overallResult = overallResult || conditionMet;
        }
      }
    }

    if ( firstCondition ) {
      return true;
    }

    return overallResult;
  });
}

function doesRowMeetFilter( row: Row, filter: FilterConditionType ): boolean {
  const { field, operator, value } = filter;
  const filterValue = value?.toLowerCase() || '';

  if ( field === 'any' ) {
    return Object.values( row.values ).some(( cellVal ) => {
      const cellValue = String( cellVal ?? '' ).toLowerCase();
      return checkOperator( cellValue, filterValue, operator! );
    });
  } else {
    const cellVal = row.values[field] ?? '';
    const cellValue = String( cellVal ).toLowerCase();
    return checkOperator( cellValue, filterValue, operator! );
  }
}

function checkOperator( cellValue: string, filterValue: string, operator: 'contains' | 'notContains' | 'exists' ): boolean {
  if ( operator === 'contains' ) {
    return cellValue.includes( filterValue );
  } else if ( operator === 'notContains' ) {
    return !cellValue.includes( filterValue );
  } else if ( operator === 'exists' ){
    return cellValue !== '';
  }
  return false;
}

function sortComparer( a: Row, b: Row, sortState: SortState ): number {
  if( sortState === null ) return 0;
  const aVal = String( a.values[sortState.columnId] ?? '' );
  const bVal = String( b.values[sortState.columnId] ?? '' );
  if ( sortState.direction === 'asc' ) {
    return aVal.localeCompare( bVal );
  } else {
    return bVal.localeCompare( aVal );
  }
}

// ---------------------------------------------------------------------------
// Visual rows pipeline (TV1-1690 / B-2b + B-3)
//
// Four independently memoized stages:
//   filter  <- rows, filters                            -> string[]
//   sort    <- filteredIds, sorting, rowsById           -> string[]
//   flat    <- sortedIds, groupBy, collapsedGroups,     -> GridFlatItem[]
//              rowsById
//   group   <- flatItems                                -> string[]  (keys, w/ markers)
//   visual  <- flatItems                                -> Row[]     (body rows only)
//
// `selectFlatItems` is the canonical projection: a single ordered list of
// items the grid renders top-to-bottom — either group-header markers or
// body rows. Collapsed groups emit ONLY their marker; their body rows are
// omitted from the flat list (B-3 / TV1-1690).
//
// `selectGroupedRowIds` and `selectVisualRows` both derive from
// `selectFlatItems` so honoring collapse lives in ONE place.
// ---------------------------------------------------------------------------

const GROUP_MARKER_PREFIX = 'group::';

export type GridFlatItem =
  | { kind: 'group'; key: string; groupValue: string; rowCount: number; collapsed: boolean }
  | { kind: 'row'; key: string; row: Row };

export const isGroupMarker = ( id: string ): boolean => id.startsWith( GROUP_MARKER_PREFIX );

export const selectFilteredRowIds = createSelector(
  [ selectAllRows, selectFilters ],
  ( rows, filters ): string[] => {
    const filtered = applyFilters( rows, filters );
    return filtered.map(( r ) => r.id );
  }
);

export const selectSortedRowIds = createSelector(
  [ selectFilteredRowIds, selectSorting, selectRowEntities ],
  ( filteredIds, sorting, entities ): string[] => {
    if ( !sorting ) return filteredIds;
    const sorted = [...filteredIds];
    sorted.sort(( aId, bId ) => {
      const a = entities[aId];
      const b = entities[bId];
      if ( !a || !b ) return 0;
      return sortComparer( a, b, sorting );
    });
    return sorted;
  }
);

// No-group branch: depends ONLY on sortedIds + entities. Memoizing it
// separately means a `collapsedGroups` change while `groupBy === null`
// does NOT invalidate the flat-items projection.
const selectFlatItemsUngrouped = createSelector(
  [ selectSortedRowIds, selectRowEntities ],
  ( sortedIds, entities ): GridFlatItem[] => {
    const out: GridFlatItem[] = [];
    for ( const id of sortedIds ) {
      const row = entities[id];
      if ( row ) out.push({ kind: 'row', key: id, row });
    }
    return out;
  }
);

const EMPTY_FLAT_ITEMS: GridFlatItem[] = [];

// Group branch: depends on collapsedSet so toggling collapse flips
// markers' `collapsed` flag and omits collapsed bodies from the flat list.
const selectFlatItemsGrouped = createSelector(
  [ selectSortedRowIds, selectGroupBy, selectCollapsedGroups, selectRowEntities ],
  ( sortedIds, groupBy, collapsedSet, entities ): GridFlatItem[] => {
    if ( !groupBy ) return EMPTY_FLAT_ITEMS;

    // Bucket sorted ids by group value. Each bucket preserves the sort order
    // of its members because we iterate sortedIds in order.
    const buckets: Record<string, string[]> = {};
    for ( const id of sortedIds ) {
      const row = entities[id];
      if ( !row ) continue;
      const groupValue = String( row.values[groupBy] ?? '(none)' );
      if ( !buckets[groupValue]) buckets[groupValue] = [];
      buckets[groupValue].push( id );
    }

    const groupKeys = Object.keys( buckets ).sort();
    const out: GridFlatItem[] = [];
    for ( const groupValue of groupKeys ) {
      const ids = buckets[groupValue];
      // `selectCollapsedGroups` returns a Set<string> keyed by the raw
      // group value (per `collapsedGroupsSlice.ts` + `GroupRow.tsx`).
      const collapsed = collapsedSet.has( groupValue );
      out.push({
        kind: 'group',
        key: `${GROUP_MARKER_PREFIX}${groupValue}`,
        groupValue,
        // BEFORE-collapse count so the header can read "Group X (5)"
        // even while the body is hidden.
        rowCount: ids.length,
        collapsed
      });
      if ( !collapsed ) {
        for ( const id of ids ) {
          const row = entities[id];
          if ( row ) out.push({ kind: 'row', key: id, row });
        }
      }
    }
    return out;
  }
);

// Public alias: switches between the two memoized branches by `groupBy`.
// When `groupBy === null`, the ungrouped projection is returned and the
// grouped branch's collapsed-set dep is not on the path — so collapsing a
// group while no `groupBy` is active does not cause a recomputation.
export const selectFlatItems = createSelector(
  [ selectGroupBy, selectFlatItemsUngrouped, selectFlatItemsGrouped ],
  ( groupBy, ungrouped, grouped ): GridFlatItem[] => {
    return groupBy ? grouped : ungrouped;
  }
);

export const selectGroupedRowIds = createSelector(
  [selectFlatItems],
  ( items ): string[] => items.map(( item ) => item.key )
);

export const selectVisualRows = createSelector(
  [selectFlatItems],
  ( items ): Row[] => {
    const out: Row[] = [];
    for ( const item of items ) {
      if ( item.kind === 'row' ) out.push( item.row );
    }
    return out;
  }
);

export const makeSelectRowById = ( rowId: string ) =>
  createSelector(
    ( state: RootState ) => selectRowById( state, rowId ),
    ( row: Row | undefined ) => row
  );

// ---------------------------------------------------------------------------
// Selection — anchor + extent range model (TV1-1690 / B-4b)
//
// Selection state is a primary range + zero or more additional ranges.
// Every range stores indices into:
//   - selectVisualRows         (post-filter/sort/group, collapsed-group bodies hidden)
//   - selectVisibleColumns     (columns.filter(c => !c.hidden))
//
// The selectors below materialize range positions to row/column IDs at read
// time. State stays O(ranges); only consumers that need a flat list (clipboard,
// batch tools) pay the materialization cost — and they need the full list
// anyway.
// ---------------------------------------------------------------------------

// Common: visible columns — every selection consumer that does index<->id
// translation needs this; extracting it kills the inline `.filter(c => !c.hidden)`
// repetition scattered through the codebase.
export const selectVisibleColumns = createSelector(
  [selectAllColumns],
  ( cols ) => cols.filter(( c ) => !c.hidden )
);

const rangeBounds = ( r: SelectionRange ) => ({
  minRow: Math.min( r.anchorRow, r.focusRow ),
  maxRow: Math.max( r.anchorRow, r.focusRow ),
  minCol: Math.min( r.anchorCol, r.focusCol ),
  maxCol: Math.max( r.anchorCol, r.focusCol )
});

const cellsInRange = ( r: SelectionRange, visualRows: Row[], visibleCols: Column[]): CellCoordinates[] => {
  const out: CellCoordinates[] = [];
  const { minRow, maxRow, minCol, maxCol } = rangeBounds( r );
  const rStart = Math.max( 0, minRow );
  const rEnd = Math.min( visualRows.length - 1, maxRow );
  const cStart = Math.max( 0, minCol );
  const cEnd = Math.min( visibleCols.length - 1, maxCol );
  for ( let row = rStart; row <= rEnd; row++ ) {
    for ( let col = cStart; col <= cEnd; col++ ) {
      out.push({ rowId: visualRows[row].id, columnId: visibleCols[col].id });
    }
  }
  return out;
};

// All ranges (primary + additional) as a single non-null list.
export const selectAllRanges = createSelector(
  [selectSelection],
  ( s ): SelectionRange[] => ( s.primary ? [ s.primary, ...s.additional ] : [...s.additional])
);

// Anchor cell — back-compat shape: { rowId, columnId } | null. Derived from
// primary.anchorRow/anchorCol via visualRows + visibleColumns.
export const selectAnchorCell = createSelector(
  [ selectSelection, selectVisualRows, selectVisibleColumns ],
  ( s, visualRows, visibleCols ): CellCoordinates | null => {
    if ( !s.primary ) return null;
    const { anchorRow, anchorCol } = s.primary;
    if ( anchorRow < 0 || anchorRow >= visualRows.length ) return null;
    if ( anchorCol < 0 || anchorCol >= visibleCols.length ) return null;
    return { rowId: visualRows[anchorRow].id, columnId: visibleCols[anchorCol].id };
  }
);

export const selectIsSelecting = createSelector(
  [selectSelection],
  ( s ) => s.isSelecting
);

// Materialize all selected cells across every range. This is the back-compat
// surface for CellClipboardLayer (copy/cut/paste) and any consumer that needs
// the flat list. O(rows × cols) for Ctrl+A, which is acceptable — STATE stays
// constant, materialization is on-demand.
export const selectSelectedCells = createSelector(
  [ selectAllRanges, selectVisualRows, selectVisibleColumns ],
  ( ranges, visualRows, visibleCols ): CellCoordinates[] => {
    if ( !ranges.length ) return [];
    // Deduplicate via composite key — overlapping ranges (e.g. ctrl-add over
    // an existing primary) shouldn't produce duplicate cells in the materialization.
    const seen = new Set<string>();
    const out: CellCoordinates[] = [];
    for ( const r of ranges ) {
      const cells = cellsInRange( r, visualRows, visibleCols );
      for ( const c of cells ) {
        const k = getCellKey( c );
        if ( !seen.has( k )) {
          seen.add( k );
          out.push( c );
        }
      }
    }
    return out;
  }
);

// Unique row IDs whose row index falls inside at least one range's row span.
export const selectSelectedRowIds = createSelector(
  [ selectAllRanges, selectVisualRows ],
  ( ranges, visualRows ): string[] => {
    if ( !ranges.length ) return [];
    const set = new Set<string>();
    for ( const r of ranges ) {
      const { minRow, maxRow } = rangeBounds( r );
      const start = Math.max( 0, minRow );
      const end = Math.min( visualRows.length - 1, maxRow );
      for ( let i = start; i <= end; i++ ) {
        set.add( visualRows[i].id );
      }
    }
    return Array.from( set );
  }
);

// Unique visible-column IDs whose column index falls inside at least one
// range's column span.
export const selectSelectedColumnIds = createSelector(
  [ selectAllRanges, selectVisibleColumns ],
  ( ranges, visibleCols ): string[] => {
    if ( !ranges.length ) return [];
    const set = new Set<string>();
    for ( const r of ranges ) {
      const { minCol, maxCol } = rangeBounds( r );
      const start = Math.max( 0, minCol );
      const end = Math.min( visibleCols.length - 1, maxCol );
      for ( let i = start; i <= end; i++ ) {
        set.add( visibleCols[i].id );
      }
    }
    return Array.from( set );
  }
);

// Rows whose entire visible column span is covered by SOME range. A range
// that goes from col 0 to lastVisibleCol selects every visible cell of its
// row span — those row IDs are "fully selected" for BatchDelete's purposes.
export const selectFullySelectedRowIds = createSelector(
  [ selectAllRanges, selectVisualRows, selectVisibleColumns ],
  ( ranges, visualRows, visibleCols ): string[] => {
    if ( !ranges.length || !visibleCols.length ) return [];
    const lastCol = visibleCols.length - 1;
    const set = new Set<string>();
    for ( const r of ranges ) {
      const { minRow, maxRow, minCol, maxCol } = rangeBounds( r );
      if ( minCol > 0 || maxCol < lastCol ) continue;
      const start = Math.max( 0, minRow );
      const end = Math.min( visualRows.length - 1, maxRow );
      for ( let i = start; i <= end; i++ ) {
        set.add( visualRows[i].id );
      }
    }
    return Array.from( set );
  }
);

export const selectSelectedRows = createSelector(
  [ selectSelectedRowIds, selectAllRows ],
  ( selectedRowIds, allRows ) => {
    return allRows.filter( row => selectedRowIds.includes( row.id ));
  }
);

// Back-compat alias for legacy consumers that used `selectSelectedCellsArray`
// to get an array of CellCoordinates. The new `selectSelectedCells` IS that
// array — exporting under both names keeps the prior import surface working.
export const selectSelectedCellsArray = selectSelectedCells;

// check if a specific cell is selected. The cell is "selected" iff the
// cell's row id appears in some visual-row slot whose row+column index falls
// inside any range. If the cell isn't currently visible (e.g. its row is in a
// collapsed group), this returns false.
export const makeSelectIsCellSelected = ( cell: CellCoordinates ) =>
  createSelector(
    [ selectAllRanges, selectVisualRows, selectVisibleColumns ],
    ( ranges, visualRows, visibleCols ): boolean => {
      if ( !ranges.length ) return false;
      const rowIdx = visualRows.findIndex(( r ) => r.id === cell.rowId );
      if ( rowIdx < 0 ) return false;
      const colIdx = visibleCols.findIndex(( c ) => c.id === cell.columnId );
      if ( colIdx < 0 ) return false;
      for ( const r of ranges ) {
        const { minRow, maxRow, minCol, maxCol } = rangeBounds( r );
        if ( rowIdx >= minRow && rowIdx <= maxRow && colIdx >= minCol && colIdx <= maxCol ) {
          return true;
        }
      }
      return false;
    }
  );

// selector factory for isFocused
export const makeSelectIsCellFocused = ( cell: CellCoordinates ) =>
    createSelector([selectFocusedCell], ( focusedCell ) =>
        focusedCell
            ? focusedCell.rowId === cell.rowId && focusedCell.columnId === cell.columnId
            : false );

// selector Factory for isEditing
export const makeSelectIsCellEditing = ( cell: CellCoordinates ) =>
    createSelector([selectEditingCell], ( editingCell ) =>
        editingCell
            ? editingCell.rowId === cell.rowId && editingCell.columnId === cell.columnId
            : false );
export const selectSelectedColumnTypes = createSelector(
  [ selectSelectedColumnIds, selectAllColumns ],
  ( selectedColumnIds, allColumns ): ColumnType[] => {
    const columnTypes = new Set<ColumnType>();

    allColumns.forEach( column => {
      if ( selectedColumnIds.includes( column.id )) {
        columnTypes.add( column.type ); 
      }
    });

    return Array.from( columnTypes );
  }
);

//get all active plugin features splitted by placement
export const selectActiveFeatures = createSelector(
  [ selectAllPluginFeatures, ( state: RootState ) => state ],
  ( pluginFeatures, fullState ) => {
    const toolbar: Feature[] = [];
    const groupRow: Feature[] = [];

    pluginFeatures.forEach(( plugin: PluginFeature ) => {
      if ( plugin.condition( fullState )) {
        plugin.features.forEach(( feat ) => {
          if ( feat.placement === 'toolbar' ) {
            toolbar.push( feat );
          } else if ( feat.placement === 'groupRow' ) {
            groupRow.push( feat );
          }
        });
      }
    });

    return { toolbar, groupRow };
  }
);


export const selectRowStatus = createSelector(( state: RootState ) => state.spreadsheet.cellChanges.present, ( _state: RootState, rowId: string ) => rowId, ( cellChanges, rowId ): CellStatusType => {
    let rowHasError = false;
    let rowHasUnsaved = false;
    
    for ( const [ cellKey, cellData ] of Object.entries( cellChanges )) {
      if ( cellKey.startsWith( `${rowId}-` )) {
        // @ts-ignore FIXME: add type for cell -> cellChanges
        if ( cellData.status === 'error' ) rowHasError = true;
        // @ts-ignore FIXME: add type for cell -> cellChanges
        if ( cellData.status === 'unsaved' ) rowHasUnsaved = true;
      }
      if ( rowHasError ) break;
    }
    if ( rowHasError ) return 'error';
    if ( rowHasUnsaved ) return 'unsaved';
    return 'none';
  });

// Lightweight membership set for cut cells (TV1-1690 / B-5a).
//
// Replaces the per-cell-subscribing `selectCutBorderMap` selector. DataRow
// reads this Set once per visible row and feeds each Cell a precomputed
// `CutEdges | null` prop via `computeCutEdges()` (see Cell/cutEdges.ts).
// Cells no longer subscribe to cut state directly.
//
// Only input is `selectCutCells` — so changes to visualRows or columns no
// longer invalidate the cut-border computation upstream. The downstream
// edge math runs lazily in DataRow's per-row memo, gated by
// `cutCellKeySet.size === 0` to skip work whenever no cut is active.
export const selectCutCellKeySet = createSelector(
  [selectCutCells],
  ( cutCells ): Set<string> => {
    const set = new Set<string>();
    for ( const c of cutCells ) {
      set.add( getCellKey( c ));
    }
    return set;
  }
);

export const selectHasUnsavedChanges = ( state: RootState ) => {
  const cellChanges = state.spreadsheet.cellChanges?.present || {};
  // @ts-ignore FIXME: add type for cell -> cellChanges
  const hasUnsavedCells = Object.values( cellChanges ).some( cell => cell.status === 'unsaved' );
 
  const deletedRows = state.spreadsheet.deletedRows?.deletedRows || [];
  const hasDeletedRows = deletedRows.length > 0;

  return hasUnsavedCells || hasDeletedRows;
};
export const selectHasGroupRowFeature = createSelector(
  [ selectAllPluginFeatures, ( state: RootState ) => state ],
  ( allFeatures, state ) => {
    return allFeatures.some(
      ( feature ) => 
        feature.features.some(( f ) => f.placement === 'groupRow' ) &&
        feature.condition( state )
    );
  }
);